// V1 source: customer/midas/components/DiagramHotspotRect.tsx
// Strategy: logic-copied
// Verified no-MUI: yes
//
// V2 hotspot SVG group — Tier A behavioral parity with V1. Logic copied
// verbatim: color/bg/label utils, opacity dimming when other hotspots are
// highlighted, pulse animation on the active step, label-pill positioning.
//
// V1 had no MUI to scrub here — pure SVG. V2 was byte-equivalent except for
// the file name + the V2 file-header convention.
//
// 2026-08-21 (DIAG-OVERLAY-A): V2 now also prefers the OPTIONAL server-served
// `hotspot.color` over the local type -> color map. V1 is legacy and stays on
// the map alone, so this is the one intentional divergence from V1.
//
// 2026-08-21 (DIAG-OVERLAY-B): V2 also renders an OPTIONAL transient flash ring
// (`flashMode`) driven by a <comp-ref> chip click. Distinct from `isActive`:
// isActive is the step-navigator's persistent "current step" pulse (indefinite
// repeat), flashMode is a one-shot ~1.6s acknowledgement that clears itself.
// Both can be on the same hotspot at once and compose fine (separate rects).
//
// 2026-10-02 (OVERLAY-LABEL-PLACEMENT-1): when the viewer passes a validated
// `labelAnchor` + `labelPill`, the label pill is drawn at the bot-served anchor
// (DiagramAnchoredLabelV2). Without them — every hotspot on SBL500 and every
// other document today — the original "pill just above the box" rule below is
// rendered exactly as before.
//
// 2026-10-02 (OVERLAY-LABEL-PLACEMENT-2): the box width is passed down too, so
// the anchored label can draw its leader on any side (right / above / below
// end on the box's right edge or horizontal centre).
//
// 2026-10-04 (OVERLAY-LABEL-HIDDEN-ONLY-1): `labelHidden` suppresses the label
// entirely (opted-in page, no anchor served = the box is reachable). The pill
// AND the leader are hover / click targets of THIS hotspot: both are rendered
// inside the same <g> that carries the box's mouse handlers, so a hover or
// click on them runs exactly the box's handlers (same tooltip, same click,
// same drag behaviour, same dimming, hidden with the overlay) — no second
// set of handlers. The leader gets a transparent hit stroke for that.
//
// 2026-10-04 (OVERLAY-CLICK-HIGHLIGHT-1, owner ruling bot b5164c5): OPTIONAL
// `isSelected` = clicked box highlighted (35 % fill, 0.4 stroke, opacity 1, and
// a pointer-events-none ring OUTSIDE the box edge). Hover is untouched.
//
// 2026-10-21 (OVERLAY-BOX-STYLE-1, bot request 2026-10-12): the box that is
// neither selected nor answer-highlighted is drawn like the bot preview —
// 2 px screen stroke inside the edge at every zoom, served color, 22 % fill,
// dashed grey with no served color (utils/diagramBoxStyle.ts). The selected /
// highlighted values above are unchanged.

import React, { useCallback, useMemo } from "react";
import {
  DiagramHotspot,
  DiagramLabelAnchor,
  DiagramLabelPill,
} from "../../../../shared/chatBot/models/chatBotModels";
import {
  getDiagramColor,
  getDiagramLabelColors,
} from "../../utils/diagramColorMap";
import { unselectedBoxProps } from "../../utils/diagramBoxStyle";
import DiagramAnchoredLabelV2 from "./DiagramAnchoredLabelV2";

// OVERLAY-CLICK-HIGHLIGHT-1, viewBox units like the 0.25/0.4/0.6/0.85 strokes.
export const SELECTED_FILL_ALPHA = "59"; // 35 % (highlighted 25 %, base 12 %)
export const SELECTED_RING_WIDTH = 0.5;

interface Props {
  hotspot: DiagramHotspot;
  isHighlighted: boolean;
  isActive: boolean;
  /** true when at least one hotspot in the set is highlighted */
  hasAnyHighlighted: boolean;
  /**
   * DIAG-OVERLAY-B: transient comp-ref flash for THIS hotspot.
   *   undefined → not flashing (the only state any pre-existing caller sees).
   *   "pulse"   → animated stroke-opacity ring (default).
   *   "static"  → non-animated ring, for `prefers-reduced-motion: reduce`.
   * The static variant is a real requirement, not a degradation: SVG SMIL is
   * outside the reach of the CSS reduced-motion override, so a reduced-motion
   * user would otherwise see the full animation.
   */
  flashMode?: "pulse" | "static";
  /**
   * OVERLAY-LABEL-PLACEMENT-1: validated `label_anchor` for THIS hotspot.
   * Used only together with `labelPill` + `imageAspect`; undefined (the only
   * state any pre-existing caller sees) = original label rule.
   */
  labelAnchor?: DiagramLabelAnchor;
  /** Validated page-level `label_pill` the anchor was computed for. */
  labelPill?: DiagramLabelPill;
  /** Rendered image width / height — converts width-% to height-% units. */
  imageAspect?: number;
  /**
   * OVERLAY-LABEL-HIDDEN-ONLY-1: true = draw NO label for this hotspot (the
   * page carries a valid `label_pill` and this hotspot has no `label_anchor`,
   * i.e. its box is reachable by the mouse). undefined (the only state any
   * pre-existing caller sees) = previous behaviour.
   */
  labelHidden?: boolean;
  /** OVERLAY-CLICK-HIGHLIGHT-1: the viewer's single selected hotspot. */
  isSelected?: boolean;
  /**
   * OVERLAY-BOX-STYLE-1: id of the viewer's shared objectBoundingBox
   * <clipPath> that keeps the unselected box's stroke inside its edge.
   */
  boxClipId?: string;
  onMouseEnter: (id: string, e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseLeave: () => void;
  onClick: (id: string) => void;
}

const DiagramHotspotRectV2: React.FC<Props> = ({
  hotspot,
  isHighlighted,
  isActive,
  hasAnyHighlighted,
  flashMode,
  labelAnchor,
  labelPill,
  imageAspect,
  labelHidden,
  isSelected,
  boxClipId,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
  onClick,
}) => {
  // Served color (bot canonical palette, optional) wins; our type -> color
  // map is the fallback when the bot omits it (labels, selected/highlighted
  // box). See diagramColorMap.ts. The unselected box uses the served color
  // only (OVERLAY-BOX-STYLE-1).
  const color = getDiagramColor(hotspot.type, hotspot.color);
  const labelColors = useMemo(
    () => getDiagramLabelColors(hotspot.type, hotspot.color),
    [hotspot.type, hotspot.color]
  );

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent) => onMouseEnter(hotspot.vi_id, e),
    [hotspot.vi_id, onMouseEnter]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClick(hotspot.vi_id);
    },
    [hotspot.vi_id, onClick]
  );

  // Determine opacity: highlighted = full, non-highlighted-when-some-highlighted = dimmed
  //
  // DIAG-OVERLAY-B: a flashing hotspot is forced to FULL opacity. Without this
  // override, clicking a comp-ref chip for a component that is not part of the
  // answer's highlighted set would paint the flash at 0.35 alpha on a busy
  // schematic — i.e. the user clicks and sees nothing. The override lasts only
  // as long as the flash. OVERLAY-CLICK-HIGHLIGHT-1: same for the selected one.
  const groupOpacity = flashMode || isSelected
    ? 1
    : hasAnyHighlighted && !isHighlighted
    ? 0.35
    : 1;

  // Selected / highlighted: deployed values (fill 35/25 %, 0.4 stroke).
  // Anything else: the bot-preview box (OVERLAY-BOX-STYLE-1).
  const emphasised = isHighlighted || isSelected;
  const boxProps = emphasised
    ? {
        fill: color + (isSelected ? SELECTED_FILL_ALPHA : "40"),
        stroke: color,
        strokeWidth: 0.4,
      }
    : unselectedBoxProps(hotspot.color, boxClipId);
  const filter = emphasised ? `drop-shadow(0 0 0.5px ${color})` : undefined;
  // Ring inflated by half its width per side: inner edge = the box edge.
  const ringInset = SELECTED_RING_WIDTH / 2;

  // Label positioning: pill just above the rect
  const labelX = hotspot.x_pct;
  const labelY = hotspot.y_pct - 0.3; // slightly above top edge
  const labelText = hotspot.label || hotspot.component_id || '';

  // Approximate label pill width (percentage-based, ~0.55% per char, min 2.5%)
  const pillWidth = Math.max(labelText.length * 0.55, 2.5);
  const pillHeight = 1.6;

  // OVERLAY-LABEL-HIDDEN-ONLY-1: no label at all for a reachable box on an
  // opted-in page.
  const showLabel = !!labelText && !labelHidden;

  // OVERLAY-LABEL-PLACEMENT-1: served anchor wins when fully usable.
  const anchored =
    showLabel && !!labelAnchor && !!labelPill && imageAspect !== undefined;

  return (
    <g
      onMouseEnter={handleMouseEnter}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onClick={handleClick}
      style={{ cursor: "pointer", opacity: groupOpacity }}
      filter={filter}
      data-selected={isSelected ? "true" : undefined}
    >
      {/* Main rectangle */}
      <rect
        x={`${hotspot.x_pct}%`}
        y={`${hotspot.y_pct}%`}
        width={`${hotspot.w_pct}%`}
        height={`${hotspot.h_pct}%`}
        {...boxProps}
        rx="0"
        ry="0"
      />

      {/* OVERLAY-CLICK-HIGHLIGHT-1 — static selection ring outside the box;
          pointer-events none, so it never widens the hit area. */}
      {isSelected && (
        <rect
          x={`${hotspot.x_pct - ringInset}%`}
          y={`${hotspot.y_pct - ringInset}%`}
          width={`${hotspot.w_pct + SELECTED_RING_WIDTH}%`}
          height={`${hotspot.h_pct + SELECTED_RING_WIDTH}%`}
          fill="none"
          stroke={color}
          strokeWidth={SELECTED_RING_WIDTH}
          pointerEvents="none"
          data-selection-ring="true"
        />
      )}

      {/* Pulse animation for active step */}
      {isActive && (
        <rect
          x={`${hotspot.x_pct}%`}
          y={`${hotspot.y_pct}%`}
          width={`${hotspot.w_pct}%`}
          height={`${hotspot.h_pct}%`}
          fill="none"
          stroke={color}
          strokeWidth={0.6}
          rx="0"
          ry="0"
        >
          <animate
            attributeName="stroke-opacity"
            values="0.3;0.8;0.3"
            dur="1.5s"
            repeatCount="indefinite"
          />
        </rect>
      )}

      {/* DIAG-OVERLAY-B — transient comp-ref flash ring.
          Drawn OUTSIDE the hotspot box (inflated by ~0.35% on each side) so it
          reads as an acknowledgement around the component rather than as a
          second highlight state of it, and so it stays legible on top of the
          isActive pulse when both are on.
          repeatCount is FINITE (3 cycles ≈ 1.6s, matching
          COMP_FLASH_DURATION_MS) — the viewer unmounts this rect when its
          timer fires, but a finite count means the ring can never survive as
          an infinite animation if a future caller forgets to clear it. */}
      {flashMode && (
        <rect
          x={`${hotspot.x_pct - 0.35}%`}
          y={`${hotspot.y_pct - 0.35}%`}
          width={`${hotspot.w_pct + 0.7}%`}
          height={`${hotspot.h_pct + 0.7}%`}
          fill="none"
          stroke={color}
          strokeWidth={0.85}
          strokeOpacity={flashMode === "static" ? 1 : undefined}
          rx="0.2"
          ry="0.2"
        >
          {flashMode === "pulse" && (
            <animate
              attributeName="stroke-opacity"
              values="1;0.2;1"
              dur="0.533s"
              repeatCount="3"
              fill="freeze"
            />
          )}
        </rect>
      )}

      {/* Label pill background */}
      {showLabel && !anchored && (
        <rect
          x={`${labelX}%`}
          y={`${labelY - pillHeight}%`}
          width={`${pillWidth}%`}
          height={`${pillHeight}%`}
          fill={labelColors.bg}
          rx="0.3"
          ry="0.3"
        />
      )}

      {/* Label text */}
      {showLabel && !anchored && (
        <text
          x={`${labelX + pillWidth / 2}%`}
          y={`${labelY - pillHeight / 2}%`}
          textAnchor="middle"
          dominantBaseline="central"
          fill={labelColors.fg}
          fontSize="0.9"
          fontFamily="'JetBrains Mono', 'Consolas', monospace"
          fontWeight="700"
        >
          {labelText}
        </text>
      )}

      {/* OVERLAY-LABEL-PLACEMENT-1 — label at the served anchor */}
      {anchored && labelAnchor && labelPill && imageAspect !== undefined && (
        <DiagramAnchoredLabelV2
          anchor={labelAnchor}
          pill={labelPill}
          labelText={labelText}
          imageAspect={imageAspect}
          color={labelColors.bg}
          textColor={labelColors.fg}
          boxX={hotspot.x_pct}
          boxY={hotspot.y_pct}
          boxW={hotspot.w_pct}
          boxH={hotspot.h_pct}
        />
      )}
    </g>
  );
};

export default React.memo(DiagramHotspotRectV2);
